// Escritura del catálogo de servicios (FRA-347/348/349/350/351). El alta, la
// edición y el asistente de clasificación comparten la MISMA forma de
// formulario y la MISMA función de guardado, para que las tres pantallas
// pidan y escriban exactamente lo mismo.
//
// Todo cae en un writeBatch: el doc del servicio, su gasto de adquisición si
// se capturó, y el historial de la tarifa del proveedor (`tarifaHistorial`).
// Un valor vigente sin su historial es imposible por construcción (FRA-340).

import {
  collection,
  doc,
  serverTimestamp,
  updateDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { asentarPrecioAcordado } from "./precioAcordado";
import { normalizeMoneyInput } from "./money";
import { todayInputValue, parseDateInputToTimestamp } from "./dates";
import {
  ESCALA_DEFAULT,
  TIPOS_SERVICIO,
  modoPiezasDe,
  noDisponiblesDe,
  nuevoIdNoDisponible,
  piezasTotalesDe,
  precioDe,
  costoProveedorDe,
  escalaDe,
  esRenta,
  incluyeDe,
  nuevaVariante,
  puedeIrIncluido,
  variantesDe,
  validarServicio,
} from "./catalogo";

// ── La forma del formulario ──────────────────────────────────────────

export const formVacio = () => ({
  nombre: "",
  descripcion: "",
  // "normal" | "renta" | "descuento". Un descuento no tiene dimensiones ni
  // variantes; una renta no tiene proveedor ni piezas, pero sí incluidos.
  tipo: "normal",
  // Solo para tipo "renta": [{conceptoId, cantidad}] con lo que trae. La
  // cantidad es el default que la cotización pre-carga y el owner ajusta.
  incluye: [],
  // Precio al cliente
  unidad: "evento",
  unidadNombre: "",
  incluidoEnRenta: false,
  precio: "",
  // 1. ¿Lo da un proveedor? (`deProveedor` es la casilla; `proveedorId` cuál)
  deProveedor: false,
  proveedorId: "",
  costoProveedor: "",
  // 2. ¿Tiene piezas físicas?
  itemizado: false,
  // "identificadas" (cada pieza es un doc, con estado y nivel) o "cantidad"
  // (un total del que se aparta cupo; nadie da de alta 240 sillas una por una).
  modoPiezas: "identificadas",
  piezasTotales: "",
  // Modo cantidad: [{id, nombre, cantidad}] de lo que existe pero no se puede
  // reservar. El nombre lo escribe el venue.
  noDisponibles: [],
  escalaTipo: "ninguna",
  escalaEtiquetas: [...ESCALA_DEFAULT.etiquetas],
  // Inversión (historia, opcional; solo en el alta)
  adquisicionMonto: "",
  adquisicionFecha: todayInputValue(),
  // Desmarcado: registrar el egreso es una decisión, no el default. La
  // compra pudo capturarse por otro lado y marcarlo solo la duplicaba.
  registrarGasto: false,
  // Variantes
  variantes: [],
});

// Carga un servicio existente en la forma del formulario. Un doc viejo (solo
// nombre y precio) entra como servicio del venue con precio plano.
export function formDesdeServicio(item) {
  const base = formVacio();
  if (!item) return base;
  const escala = escalaDe(item);
  const e = item.escalaUnidades || null;
  return {
    ...base,
    nombre: item.nombre || "",
    descripcion: item.descripcion || "",
    tipo: esRenta(item)
      ? "renta"
      : (item.tipo || "normal") === "descuento"
      ? "descuento"
      : "normal",
    incluye: incluyeDe(item),
    unidad: item.unidad || "evento",
    unidadNombre: item.unidadNombre || "",
    incluidoEnRenta: item.cobrable === false,
    precio: precioDe(item) ? String(precioDe(item)) : "",
    deProveedor: !!item.proveedorId,
    proveedorId: item.proveedorId || "",
    costoProveedor: costoProveedorDe(item) ? String(costoProveedorDe(item)) : "",
    itemizado: !!item.itemizado,
    modoPiezas: modoPiezasDe(item),
    piezasTotales: piezasTotalesDe(item) ? String(piezasTotalesDe(item)) : "",
    noDisponibles: noDisponiblesDe(item).map((x) => ({
      id: x.id || nuevoIdNoDisponible(),
      nombre: x.nombre,
      cantidad: String(x.cantidad),
    })),
    escalaTipo: escala ? escala.tipo : "ninguna",
    escalaEtiquetas:
      escala?.tipo === "etiquetas"
        ? [...escala.etiquetas]
        : Array.isArray(e?.etiquetas) && e.etiquetas.length
        ? [...e.etiquetas]
        : [...ESCALA_DEFAULT.etiquetas],
    variantes: variantesDe(item).map((v) => ({
      id: v.id,
      nombre: v.nombre || "",
      precio: v.precioUnitario != null ? String(v.precioUnitario) : "",
      costoProveedor: v.costoProveedor != null ? String(v.costoProveedor) : "",
    })),
  };
}

// Normaliza el formulario para validarlo con validarServicio: los montos de
// texto se vuelven números, las etiquetas se limpian.
export function formNormalizado(form) {
  const esDesc = form.tipo === "descuento";
  const esRentaForm = form.tipo === "renta";
  const proveedorId = !esDesc && !esRentaForm && form.deProveedor ? form.proveedorId || "" : "";
  return {
    ...form,
    proveedorId,
    // Una renta es del venue, se cobra y no lleva piezas: se normaliza aquí y
    // no solo escondiendo los bloques del formulario, para que quien llenó
    // proveedor y después cambió a renta no guarde un híbrido.
    incluye: esRentaForm ? incluyeDe({ incluye: form.incluye }) : [],
    incluidoEnRenta: esRentaForm ? false : form.incluidoEnRenta,
    // Un descuento no lleva dimensiones ni variantes, diga lo que diga el
    // formulario: así un doc viejo con `tipo: "descuento"` no puede acabar
    // con piezas por accidente.
    deProveedor: esDesc || esRentaForm ? false : form.deProveedor,
    // Excluyentes (ver utils/catalogo.js): si lo trae un proveedor, las
    // piezas no son del venue. Se normaliza aquí y no solo en el formulario,
    // para que ningún caller pueda guardar un servicio con las dos.
    itemizado: esDesc || esRentaForm || proveedorId ? false : form.itemizado,
    nombre: String(form.nombre || "").trim(),
    costoProveedor: proveedorId ? Number(normalizeMoneyInput(form.costoProveedor)) || 0 : 0,
    escalaEtiquetas: (form.escalaEtiquetas || []).map((x) => String(x || "").trim()).filter(Boolean),
    modoPiezas: form.modoPiezas === "cantidad" ? "cantidad" : "identificadas",
    piezasTotales: Math.max(0, Math.floor(Number(form.piezasTotales) || 0)),
    noDisponibles: (Array.isArray(form.noDisponibles) ? form.noDisponibles : []).map((x) => ({
      id: x?.id || nuevoIdNoDisponible(),
      nombre: String(x?.nombre || "").trim(),
      cantidad: Math.max(0, Math.floor(Number(x?.cantidad) || 0)),
    })),
    precioUnitario:
      form.incluidoEnRenta && !esRentaForm ? 0 : Number(normalizeMoneyInput(form.precio)) || 0,
    variantes: (esDesc ? [] : form.variantes || []).map((v) => ({
      ...v,
      nombre: String(v.nombre || "").trim(),
      precioUnitario: Number(normalizeMoneyInput(v.precio)) || 0,
      costoProveedor: proveedorId ? Number(normalizeMoneyInput(v.costoProveedor)) || 0 : 0,
    })),
  };
}

// `conceptosPorId` es opcional: con él se valida que lo que la renta incluye
// siga existiendo y pueda ir dentro (ni otra renta, ni un descuento, ni ella
// misma). Sin él —cuando el caller no tiene el catálogo a la mano— la lista
// se guarda como está y `docDesdeForm` la filtra igual.
export const validarForm = (form, { conceptosPorId = null, itemId = null } = {}) => {
  if (form.deProveedor && !form.proveedorId && form.tipo !== "renta")
    return "Elige qué proveedor lo da.";
  const error = validarServicio(formNormalizado(form));
  if (error) return error;
  if (form.tipo === "renta" && conceptosPorId) {
    for (const { conceptoId } of incluyeDe({ incluye: form.incluye })) {
      const hijo = conceptosPorId[conceptoId];
      if (!hijo) return "Uno de los servicios incluidos ya no existe en el catálogo. Quítalo de la lista.";
      if (!puedeIrIncluido(hijo, itemId))
        return `“${hijo.nombre || "Ese servicio"}” no puede ir dentro de una renta.`;
    }
  }
  return null;
};

// Los campos del doc que salen del formulario. `actual` es el doc existente
// (edición/clasificación) para conservar lo que el formulario no toca:
// existencias, adquisición, paquetes.
export function docDesdeForm(
  form,
  actual = null,
  { proveedoresPorId = {}, conceptosPorId = null, itemId = null } = {}
) {
  const f = formNormalizado(form);
  const proveedorId = f.proveedorId || null;
  const costoProveedor = proveedorId && f.costoProveedor > 0 ? f.costoProveedor : null;
  const tipo = TIPOS_SERVICIO.includes(f.tipo)
    ? f.tipo
    : actual?.tipo && TIPOS_SERVICIO.includes(actual.tipo)
    ? actual.tipo
    : "normal";
  // Con el catálogo a la mano se filtra lo que ya no puede ir dentro (se
  // archivó, se volvió renta): la invariante "un nivel, sin ciclos" se
  // sostiene en la escritura, no en que el picker se haya portado bien.
  const incluye =
    tipo === "renta"
      ? f.incluye.filter(
          ({ conceptoId }) =>
            !conceptosPorId || puedeIrIncluido(conceptosPorId[conceptoId], itemId)
        )
      : [];
  return {
    nombre: f.nombre,
    tipo,
    incluye,
    descripcion: String(form.descripcion || "").trim() || null,
    unidad: f.unidad || "evento",
    unidadNombre: f.unidad === "otra" ? String(form.unidadNombre || "").trim() || null : null,
    cobrable: !f.incluidoEnRenta,
    precioUnitario: f.precioUnitario,
    // 1. Proveedor
    proveedorId,
    proveedorNombre: proveedorId ? proveedoresPorId[proveedorId]?.nombre || actual?.proveedorNombre || null : null,
    costoProveedor,
    // 2. Piezas
    itemizado: !!f.itemizado,
    modoPiezas: f.itemizado ? f.modoPiezas : null,
    // El total y los renglones fuera de circulación solo existen en el modo por
    // cantidad: en las identificadas el total ES cuántas piezas hay dadas de
    // alta y su estado vive en cada una.
    piezasTotales: f.itemizado && f.modoPiezas === "cantidad" ? f.piezasTotales : null,
    noDisponibles:
      f.itemizado && f.modoPiezas === "cantidad"
        ? f.noDisponibles.filter((x) => x.nombre && x.cantidad > 0)
        : [],
    escalaUnidades: f.itemizado && f.modoPiezas !== "cantidad"
      ? f.escalaTipo === "etiquetas"
        ? { tipo: "etiquetas", etiquetas: f.escalaEtiquetas }
        : f.escalaTipo === "porcentaje"
        ? { tipo: "porcentaje" }
        : { tipo: "ninguna" }
      : null,
    // Variantes: cada una con su estructura de cobro (y de costo si aplica).
    variantes: f.variantes.map((v) =>
      nuevaVariante({
        id: v.id || null,
        nombre: v.nombre,
        precioUnitario: v.precioUnitario,
        costoProveedor: proveedorId ? v.costoProveedor : null,
      })
    ),
    // Las tres preguntas ya se contestaron (aunque sea "no").
    clasificadoEn: actual?.clasificadoEn || serverTimestamp(),
  };
}

// Registros de historial que un guardado genera, en el mismo batch que el
// valor vigente:
//   - tarifa del proveedor → conceptos/{id}/tarifaHistorial
function asentarHistoriales(batch, { itemRef, actual, docNuevo, user, userInfo }) {
  const costoAnterior = costoProveedorDe(actual);
  const costoNuevo = Number(docNuevo.costoProveedor) || 0;
  if (costoNuevo !== costoAnterior) {
    asentarPrecioAcordado(batch, {
      docRef: itemRef,
      subcoleccion: "tarifaHistorial",
      monto: costoNuevo,
      montoAnterior: costoAnterior,
      notas: costoNuevo ? null : "El servicio dejó de tener costo de proveedor",
      user,
      userInfo,
    });
  }
}

// Inversión (opcional): se guarda en el doc y, si se pide, como gasto real
// en Cuentas. `origen: "adquisicion"` para que la recuperación no lo cuente
// como costo de uso. La usan el alta y la clasificación (un concepto viejo
// también pudo haberse comprado).
//
// Solo aplica a lo que es del venue: no compras lo que trae un proveedor. Se
// descarta aquí y no solo escondiendo el bloque, porque quien llenó la
// inversión y después cambió a "un proveedor" dejaría el monto en el
// formulario y acabaría con un egreso que nunca ocurrió.
function inversionDesdeForm(form) {
  if (form.deProveedor && form.proveedorId) {
    return { adquisicion: null, fechaAdq: null, inversion: 0 };
  }
  const inversion = Number(normalizeMoneyInput(form.adquisicionMonto)) || 0;
  if (!(inversion > 0)) return { adquisicion: null, fechaAdq: null, inversion: 0 };
  const fechaAdq = parseDateInputToTimestamp(form.adquisicionFecha, null) || Timestamp.now();
  return {
    inversion,
    fechaAdq,
    adquisicion: { monto: inversion, fecha: fechaAdq, notas: null, registradoComoGasto: !!form.registrarGasto },
  };
}

function asentarGastoAdquisicion(batch, { venueId, itemRef, nombre, inversion, fechaAdq, user }) {
  batch.set(doc(collection(db, "venues", venueId, "costos")), {
    eventId: null,
    concepto: `Compra de ${nombre}`,
    categoria: "Equipo",
    proveedorId: null,
    proveedorNombre: null,
    conceptoId: itemRef.id,
    conceptoNombre: nombre,
    monto: inversion,
    montoOriginal: null,
    fecha: fechaAdq,
    estado: "pagado",
    fechaPago: fechaAdq,
    origen: "adquisicion",
    metodo: null,
    notas: null,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: null,
    updatedBy: null,
  });
}

// Alta: el servicio nace completo en un solo batch. Devuelve el id.
export async function crearServicio({
  venueId,
  form,
  user,
  userInfo,
  proveedoresPorId = {},
  conceptosPorId = null,
}) {
  if (!venueId || !user?.uid) throw new Error("Sin venue o sin usuario");
  const error = validarForm(form, { conceptosPorId });
  if (error) throw new Error(error);
  const docNuevo = docDesdeForm(form, null, { proveedoresPorId, conceptosPorId });
  const itemRef = doc(collection(db, "venues", venueId, "conceptos"));
  const batch = writeBatch(db);

  const { adquisicion, fechaAdq, inversion } = inversionDesdeForm(form);

  batch.set(itemRef, {
    ...docNuevo,
    adquisicion,
    paquetes: [],
    archivedAt: null,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });

  if (adquisicion && form.registrarGasto) {
    asentarGastoAdquisicion(batch, { venueId, itemRef, nombre: docNuevo.nombre, inversion, fechaAdq, user });
  }
  asentarHistoriales(batch, { itemRef, actual: null, docNuevo, user, userInfo });
  await batch.commit();
  return itemRef.id;
}

// Edición y clasificación: el mismo guardado sobre un doc existente.
export async function guardarServicio({
  venueId,
  itemId,
  actual,
  form,
  user,
  userInfo,
  proveedoresPorId = {},
  conceptosPorId = null,
}) {
  if (!venueId || !itemId || !user?.uid) throw new Error("Sin venue o sin usuario");
  const error = validarForm(form, { conceptosPorId, itemId });
  if (error) throw new Error(error);
  const docNuevo = docDesdeForm(form, actual, { proveedoresPorId, conceptosPorId, itemId });
  const itemRef = doc(db, "venues", venueId, "conceptos", itemId);
  const batch = writeBatch(db);
  // La inversión solo se captura si el doc no la tenía (clasificación de un
  // concepto viejo); una vez registrada, se edita desde la ficha.
  const { adquisicion, fechaAdq, inversion } = actual?.adquisicion
    ? { adquisicion: null, fechaAdq: null, inversion: 0 }
    : inversionDesdeForm(form);
  batch.update(itemRef, {
    ...docNuevo,
    ...(adquisicion ? { adquisicion } : {}),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  if (adquisicion && form.registrarGasto) {
    asentarGastoAdquisicion(batch, { venueId, itemRef, nombre: docNuevo.nombre, inversion, fechaAdq, user });
  }
  asentarHistoriales(batch, { itemRef, actual, docNuevo, user, userInfo });
  await batch.commit();
  return itemId;
}

// En qué rentas va un servicio incluido. Escribe la lista `incluye` de las
// rentas elegidas y la limpia de las demás, todo en un batch: la verdad de
// "esto va dentro de aquello" vive en la renta, así que acomodar un servicio
// es editar a sus padres, no al hijo.
//
// Idempotente: agregar lo que ya estaba no duplica ni pisa la cantidad
// configurada.
export async function asignarIncluido({
  venueId,
  conceptoId,
  rentas = [],
  rentaIdsElegidas = [],
  cantidad = 1,
  user,
}) {
  if (!venueId || !conceptoId || !user?.uid) throw new Error("Sin venue, servicio o usuario");
  const elegidas = new Set(rentaIdsElegidas || []);
  const cant = Number(cantidad) > 0 ? Number(cantidad) : 1;
  const batch = writeBatch(db);
  let tocadas = 0;

  (rentas || []).forEach((renta) => {
    if (!renta?.id) return;
    const actual = incluyeDe(renta);
    const tiene = actual.some((x) => x.conceptoId === conceptoId);
    const debeTener = elegidas.has(renta.id);
    if (tiene === debeTener) return;
    const incluye = debeTener
      ? [...actual, { conceptoId, cantidad: cant }]
      : actual.filter((x) => x.conceptoId !== conceptoId);
    batch.update(doc(db, "venues", venueId, "conceptos", renta.id), {
      incluye,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
    tocadas += 1;
  });

  if (tocadas === 0) return 0;
  await batch.commit();
  return tocadas;
}

// Variante al vuelo (FRA-352): "se puede cotizar una renta de 12 horas que
// tal vez no la teníamos antes". Se crea desde la cotización y QUEDA en el
// catálogo, para que la próxima vez ya exista. Solo agrega la variante al
// array del servicio; todo lo demás del doc se conserva. Devuelve la
// variante creada (con su id) para que el caller la seleccione de inmediato.
export async function agregarVariante({
  venueId,
  item,
  nombre,
  precio,
  costoProveedor = null,
  user,
}) {
  if (!venueId || !item?.id || !user?.uid) throw new Error("Sin venue, servicio o usuario");
  const nombreLimpio = String(nombre || "").trim();
  if (!nombreLimpio) throw new Error("Ponle nombre a la variante.");
  const cobro = Number(normalizeMoneyInput(String(precio ?? ""))) || 0;
  if (!(cobro > 0)) throw new Error("La variante necesita su precio al cliente.");
  const costo = item.proveedorId ? Number(normalizeMoneyInput(String(costoProveedor ?? ""))) || 0 : 0;
  if (costo < 0) throw new Error("El costo del proveedor no puede ser negativo.");
  const existentes = variantesDe(item);
  if (existentes.some((v) => (v?.nombre || "").trim().toLowerCase() === nombreLimpio.toLowerCase())) {
    throw new Error("Ya existe una variante con ese nombre.");
  }
  const variante = nuevaVariante({ nombre: nombreLimpio, precioUnitario: cobro, costoProveedor: costo });
  await updateDoc(doc(db, "venues", venueId, "conceptos", item.id), {
    variantes: [...existentes, variante],
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  return variante;
}
